/**
 * Life4Earth V2 — User presence store.
 *
 * Tracks online/offline status for all users via SignalR PresenceChanged events.
 * Provides a real-time view of who's online, ideal for displaying green dots
 * on profile pictures and avatars throughout the app.
 */

type PresenceStatus = 'online' | 'idle' | 'offline';

export interface UserPresence {
  userGuid: string;
  status: PresenceStatus;
  connectedAt?: string;
  disconnectedAt?: string;
  lastActivityAt?: string;
}

/* ------------------------------------------------------------------ */
/*  Store state                                                        */
/* ------------------------------------------------------------------ */

let presenceMap = new Map<string, UserPresence>();
let listeners: Array<() => void> = [];
let snapshotVersion = 0;
let emitTimeoutId: NodeJS.Timeout | null = null;

/**
 * Diagnostics are gated behind the app-wide debug flag (localStorage
 * `l4e_debug_mode`, the same toggle App.tsx threads as `debugMode`). Off by
 * default so presence churn doesn't spam the console on every reload.
 */
function isDebugEnabled(): boolean {
  try {
    return (
      typeof localStorage !== "undefined" &&
      localStorage.getItem("l4e_debug_mode") === "true"
    );
  } catch {
    return false;
  }
}

/* ------------------------------------------------------------------ */
/*  Public API                                                         */
/* ------------------------------------------------------------------ */

/** Get presence status for a specific user. Defaults to 'offline' if unknown. */
export function getUserPresence(userGuid: string): PresenceStatus {
  // Normalize to uppercase to handle GUID case differences
  const normalizedGuid = userGuid.toUpperCase();
  return presenceMap.get(normalizedGuid)?.status ?? 'offline';
}

/** Check if a user is online. */
export function isUserOnline(userGuid: string): boolean {
  return getUserPresence(userGuid) === 'online';
}

/** Get all users currently online. Returns GUIDs in uppercase. */
export function getOnlineUsers(): string[] {
  const online: string[] = [];
  for (const [guid, presence] of presenceMap.entries()) {
    if (presence.status === 'online') {
      online.push(guid); // Already uppercase from updatePresence
    }
  }
  return online;
}

/** Update presence for a user. Called by SignalR event handler. */
export function updatePresence(data: {
  userGuid: string;
  status: PresenceStatus;
  connectedAt?: string;
  disconnectedAt?: string;
}): void {
  // Normalize to uppercase to handle GUID case differences
  const normalizedGuid = data.userGuid.toUpperCase();
  const existing = presenceMap.get(normalizedGuid);
  // Only emit if status actually changed
  const statusChanged = !existing || existing.status !== data.status;

  if (statusChanged) {
    if (isDebugEnabled()) {
      console.debug('[PresenceStore] statusChanged:', normalizedGuid, existing?.status ?? 'none', '→', data.status);
    }
    const updated: UserPresence = {
      userGuid: normalizedGuid,
      status: data.status,
      connectedAt: data.connectedAt ?? existing?.connectedAt,
      disconnectedAt: data.disconnectedAt ?? existing?.disconnectedAt,
      lastActivityAt: new Date().toISOString(),
    };
    presenceMap.set(normalizedGuid, updated);
    snapshotVersion++; // Increment version to trigger useSyncExternalStore updates
    emit();
  }
}

/** Clear all presence data (useful on logout). */
export function clearPresence(): void {
  presenceMap.clear();
  emit();
}

/* ------------------------------------------------------------------ */
/*  Subscription (React integration via useSyncExternalStore)          */
/* ------------------------------------------------------------------ */

/** Subscribe to presence changes. Returns an unsubscribe function. */
export function subscribe(listener: () => void): () => void {
  listeners.push(listener);
  return () => {
    listeners = listeners.filter((l) => l !== listener);
  };
}

/**
 * Snapshot for React useSyncExternalStore.
 * Returns snapshotVersion which changes only when presence status actually changes.
 * This simple number triggers re-renders in components using usePresence().
 */
export function getSnapshot(): number {
  return snapshotVersion;
}

/* ------------------------------------------------------------------ */
/*  Internal                                                           */
/* ------------------------------------------------------------------ */

function emit(): void {
  // Debounce emit to prevent rapid cascading updates
  if (emitTimeoutId !== null) {
    clearTimeout(emitTimeoutId);
  }
  emitTimeoutId = setTimeout(() => {
    listeners.forEach((fn) => fn());
    emitTimeoutId = null;
  }, 0);
}
